import { useState, useRef, useCallback, useEffect } from "react";
import { GameAudioEngine, type RevealMode, type Stage, type GameTrack, type StartMode } from "../audio";
import type { PlaybackState } from "../types/audio";

export interface UseGameEngineOptions {
  revealMode?: RevealMode;
  onTrackError?: (error: string) => void;
}

export function useGameEngine(options: UseGameEngineOptions = {}) {
  const engineRef = useRef<GameAudioEngine | null>(null);
  const readyRef = useRef(false);
  const [stage, setStage] = useState<Stage>(1);
  const [playback, setPlayback] = useState<PlaybackState>({
    status: "idle",
    currentTime: 0,
    duration: 0,
  });
  const [track, setTrack] = useState<GameTrack | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const engine = new GameAudioEngine({
      revealMode: options.revealMode ?? "notes",
    });
    engineRef.current = engine;
    const unsub = engine.onStateChange(setPlayback);
    return () => { unsub(); engine.stop(); };
  }, [options.revealMode]);

  const loadTrack = useCallback(async (artist: string, title: string, start?: StartMode, itunesId?: string): Promise<GameTrack | null> => {
    const engine = engineRef.current;
    if (!engine) return null;

    setLoading(true);
    readyRef.current = false;
    setReady(false);
    setError(null);
    try {
      const gameTrack = await engine.loadTrack(artist, title, start, undefined, itunesId);
      setTrack(gameTrack);
      setStage(1);
      readyRef.current = true;
      setReady(true);
      return gameTrack;
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAndPlay = useCallback(async (artist: string, title: string, start?: StartMode, itunesId?: string) => {
    const engine = engineRef.current;
    if (!engine) return;

    setLoading(true);
    readyRef.current = false;
    setReady(false);
    setError(null);
    try {
      const gameTrack = await engine.loadTrack(artist, title, start, undefined, itunesId);
      setTrack(gameTrack);
      setStage(1);
      readyRef.current = true;
      setReady(true);
      await engine.playCurrentStage();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load track";
      setError(msg);
      options.onTrackError?.(msg);
      // Auto-clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  const play = useCallback(async () => {
    if (!readyRef.current) return;
    try {
      await engineRef.current?.playCurrentStage();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Playback failed");
    }
  }, []);

  const pause = useCallback(() => {
    engineRef.current?.pause();
  }, []);

  const skip = useCallback(async () => {
    const engine = engineRef.current;
    if (!engine || !readyRef.current) return false;
    const hasNext = await engine.nextStage();
    if (hasNext) setStage(engine.getStage());
    return hasNext;
  }, []);

  const reveal = useCallback(async () => {
    if (!readyRef.current) return;
    await engineRef.current?.playFull();
  }, []);

  const score = track ? Math.max(0, 7 - stage) : 0;

  return {
    stage,
    playback,
    track,
    loading,
    ready,
    error,
    score,
    loadTrack,
    loadAndPlay,
    play,
    pause,
    skip,
    reveal,
  };
}
